import React, { MutableRefObject, useMemo, useRef, useState } from 'react'
import {
  Image,
  LayoutChangeEvent,
  NativeScrollEvent,
  ScrollView,
  useWindowDimensions,
  View,
} from 'react-native'
import { useNavigation } from '@react-navigation/native'
import { Selector, useSelector } from 'react-redux'
import { StyleService, useStyleSheet } from '@src/style/service'
import { TimelineChart } from '@components'
import { ThresholdType } from '@screens/Settings/models/settings.types'
import { ChartTypes, PLACEHOLDER_TEXT_BY_CHART_TYPE } from '@src/components/Timeline/constants'
import { Analytics, CustomEventTypes, Device } from '@src/config'
import { MobileAppFeature } from '@src/types'
import { User } from '@src/utils'
import { blurredGlucoseChartImage } from '@assets/images'
import { useIsDarkMode } from '@src/config/theme'
import { useShowByosIntegrationWarning } from '@src/screens/OwnSensorOnboarding/hooks/useShowByosIntegrationWarning'
import { ByosIntegrationWarning } from '@src/components/Timeline/ByosIntegrationWarning'
import { RootStoreState } from '@src/models/app.types'
import { TimelineHeader } from '@src/screens/Events/components/TimelineHeader'
import { UpsellCGMBanner } from '@src/screens/Home/components/Banners/UpsellCGMBanner'
import { eventsChartsSelector } from '@src/models/dailyCharts/selectors'
import { eventsForDateSelector } from '@src/selectors/events'
import { EventsItemType } from '@src/screens/Events/models/events.types'
import { DateRangeWarning } from './DateRangeWarning'

export interface TimelineProps {
  primaryChartRef: MutableRefObject<any>
  secondaryChartRef: MutableRefObject<any>
  onChartLoadEnd: () => void
  onChartHover?: (payload: any) => void
  hideMultiSlider?: boolean
  hideChartHeader?: boolean
  showLastReadingTooltip?: boolean
  multiSliderValuesChange?: (values: [number, number]) => void
  chartsSelector?: Selector<RootStoreState, { primary: any; secondary: any }>
  displayDateRangeWarning?: boolean
  displayGlucoseRangeIcon?: boolean
  headerRightAdornment?: React.ReactNode
  customChartHeight?: number
  isKetoneChartIncluded: boolean
}

const CHART_WIDTH_HEIGHT_RATIO = 1.6
const MAX_CHART_HEIGHT = 330

export const Timeline = React.memo((props: TimelineProps) => {
  const {
    primaryChartRef,
    secondaryChartRef,
    onChartLoadEnd,
    onChartHover,
    hideMultiSlider = false,
    hideChartHeader = false,
    showLastReadingTooltip = false,
    multiSliderValuesChange,
    customChartHeight,
    displayDateRangeWarning = false,
    displayGlucoseRangeIcon = true,
    headerRightAdornment = null,
    isKetoneChartIncluded,
    chartsSelector = eventsChartsSelector<EventsItemType>(eventsForDateSelector),
  } = props

  const styles = useStyleSheet(themedStyles)
  const navigation = useNavigation()
  const scrollRef = useRef<any>(undefined)
  const [selectedChartIndex, setSelectedChartIndex] = useState(0)

  const { primary, secondary } = useSelector(chartsSelector)
  const isDarkMode = useIsDarkMode()
  const { thirdPartyWarning } = useShowByosIntegrationWarning()
  const showByosIntegrationWarning = thirdPartyWarning
  const canViewGlucoseCharts = User.hasFeature(MobileAppFeature.ViewGlucoseCharts)

  const dimensions = useWindowDimensions()
  const [contentWidth, setContentWidth] = useState(dimensions.width)

  const onNavigateToCard = (index: number, contentWidth: number) => {
    if (!scrollRef.current) {
      return
    }

    scrollRef.current.scrollTo({ x: contentWidth * index, y: 0, animated: true })
  }

  const handleSelectChartIndex = (index: number) => {
    onNavigateToCard(index, contentWidth)
  }

  const onLayout = ({ nativeEvent }: LayoutChangeEvent) => {
    const newContentWidth = nativeEvent.layout.width
    if (!newContentWidth) {
      return
    }

    if (newContentWidth !== contentWidth) {
      onNavigateToCard(selectedChartIndex, newContentWidth)
    }
    setContentWidth(newContentWidth)
  }

  const onScroll = ({ nativeEvent }: { nativeEvent: NativeScrollEvent }) => {
    const index = Math.round(nativeEvent.contentOffset.x / contentWidth)
    if (index !== selectedChartIndex) {
      Analytics.track(CustomEventTypes.EventsSelectChart, {
        type: index === 0 ? 'glucose' : 'ketone',
      })
      setSelectedChartIndex(index)
    }
  }

  const onZoomChart = () => {
    const type = selectedChartIndex === 0 ? ChartTypes.Glucose : ChartTypes.Ketones
    navigation.navigate('ZoomChart', {
      type,
      forceLandscapeOrientation: !Device.hasLargeScreen(dimensions),
    })
  }

  const onInfoPress = () => {
    navigation.navigate('TimelineTutorialModal', {
      timeline: selectedChartIndex === 0 ? 'Glucose' : 'Ketone',
    })
  }

  const onSettingsPress = () => {
    if (selectedChartIndex === 0) {
      navigation.navigate('GlucoseThresholdSettings', { source: 'Events' })
    } else {
      navigation.navigate('ThresholdSettings', { type: ThresholdType.Ketone, source: 'Events' })
    }
  }

  const chartItems = useMemo(
    () => (isKetoneChartIncluded ? ['Glucose Chart', 'Ketone Chart'] : ['Glucose Chart']),
    [isKetoneChartIncluded],
  )

  const chartStyle = {
    width: contentWidth,
  }

  const height = customChartHeight
    ? customChartHeight
    : Math.min(contentWidth / CHART_WIDTH_HEIGHT_RATIO, MAX_CHART_HEIGHT)

  const headerComponent = (
    <TimelineHeader
      title={chartItems[selectedChartIndex]}
      charts={chartItems}
      testID="Timeline"
      selectedChartIndex={selectedChartIndex}
      onSelectChartIndex={handleSelectChartIndex}
      displayGlucoseRangeIcon={displayGlucoseRangeIcon}
      rightAdornment={headerRightAdornment}
      onExpandPress={onZoomChart}
      onInfoPress={onInfoPress}
      onSettingsPress={onSettingsPress}
    />
  )

  if (!canViewGlucoseCharts) {
    return (
      <UpsellCGMBanner
        image={
          <Image
            style={{ height, width: contentWidth }}
            source={blurredGlucoseChartImage(isDarkMode).imageSource}
            resizeMode="stretch"
          />
        }
        header={!hideChartHeader && headerComponent}
      />
    )
  }

  if (showByosIntegrationWarning) {
    return (
      <>
        {!hideChartHeader && headerComponent}
        <View>
          <Image
            style={{ height, width: contentWidth }}
            source={blurredGlucoseChartImage(isDarkMode).imageSource}
            resizeMode="stretch"
          />
          <ByosIntegrationWarning thirdPartyWarning={thirdPartyWarning} />
        </View>
      </>
    )
  }

  if (displayDateRangeWarning) {
    return (
      <>
        {!hideChartHeader && headerComponent}
        <View>
          <Image
            style={{ height, width: contentWidth }}
            source={blurredGlucoseChartImage(isDarkMode).imageSource}
            resizeMode="stretch"
          />
          <DateRangeWarning />
        </View>
      </>
    )
  }

  return (
    <View style={styles.container}>
      {!hideChartHeader && headerComponent}
      <ScrollView
        ref={scrollRef}
        horizontal
        pagingEnabled
        scrollEnabled={false}
        bounces={false}
        showsHorizontalScrollIndicator={false}
        onScroll={onScroll}
        onLayout={onLayout}
        scrollEventThrottle={0}
        style={styles.scrollView}
        contentContainerStyle={{ height }}
      >
        <TimelineChart
          chartRef={primaryChartRef}
          style={chartStyle}
          data={primary}
          type={ChartTypes.Glucose}
          onChange={onChartHover}
          onLoadEnd={onChartLoadEnd}
          multiSliderValuesChange={multiSliderValuesChange}
          placeholder={null}
          hideMultiSlider={hideMultiSlider}
          showLastReadingTooltip={showLastReadingTooltip}
        />
        {isKetoneChartIncluded && (
          <TimelineChart
            chartRef={secondaryChartRef}
            style={chartStyle}
            data={secondary}
            type={ChartTypes.Ketones}
            onChange={onChartHover}
            onLoadEnd={onChartLoadEnd}
            placeholder={PLACEHOLDER_TEXT_BY_CHART_TYPE[ChartTypes.Ketones]}
            showPoints
            connectNulls
            hideMultiSlider={hideMultiSlider}
          />
        )}
      </ScrollView>
    </View>
  )
})

const themedStyles = StyleService.create({
  scrollView: {
    flexGrow: 0,
  },
  container: {
    backgroundColor: 'theme.background',
  },
})
